import React from 'react';
import { useSuggestedProducts } from '@jbhi-fi/components/src/hooks/useSuggestedProducts';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { CarouselTabs, CarouselTabsProps, Tab } from '@jbhi-fi/components/src/CarouselTabs/CarouselTabs';
import { ChipTab, ChipTabProps } from '@jbhi-fi/components/src/CarouselTabs/ChipTab/ChipTab';
import { EnhancedProductCarousel } from '@jbhi-fi/components/src/Attach/EnhancedProductCarousel/EnhancedProductCarousel';
import ProductService from '../pdp/productService/ProductService';
import { styles } from './AttachMyCartBlock.css';
import { formatMoney } from '../../../utils/FormatMoney';
import { LineItemProps } from '@jbhi-fi/apis/src/services/types';
import { TrackingProvider } from '@jbhi-fi/components/src/Tracking';
import { IsAttachedService } from '../pdp/utils/product-service';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { ServiceIds } from '../../../utils/constants/services';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';

type TabProps = Parameters<Tab['renderLabel']>[0];

export type AttachMyCartBlockProps = {
  purchasedProductSku: string;
  serviceSuggestions?: Array<LineItemProps>;
  isServicesLoading?: boolean;
  cartProduct: JbCartItem;
};

export const AttachMyCartBlock = ({
  purchasedProductSku,
  serviceSuggestions,
  isServicesLoading,
  cartProduct,
}: AttachMyCartBlockProps) => {
  const attachConfig = window.Jbhifi?.attachConfig;
  const cartSmartModuleSettings = window.smartModuleSettings?.cartPage;
  const isProductsModuleDisabled = attachConfig?.excludedSkus?.includes(purchasedProductSku);
  const { products: [purchasedProduct] = [], isLoading: isPurchasedProductLoading } = useProducts({
    productSkus: [purchasedProductSku],
    options: { shouldResolveExtraOnlineProductFields: true },
  });
  const { cart } = useCartContext();

  const isEnhancedGoogleRecommendationsEnabled = useFeatureFlag('isEnhancedGoogleRecommendationsEnabled');

  const { suggestions, isLoading: isSuggestedProductsLoading } = useSuggestedProducts({
    products: [purchasedProduct],
    googleRecNumProducts: cartSmartModuleSettings?.productCount ?? 12,
    googleRecModel: cartSmartModuleSettings?.googleRecModelType ?? 'FBT',
    googleRecAcceptedDeviationPercentage: window.Jbhifi?.smartModuleSettings?.googleAcceptedDeviationPercentage,
    excludedSkus: cart?.items.map(item => item.sku),
    shouldEnhanceSuggestions: !!isEnhancedGoogleRecommendationsEnabled,
  });

  const productCartId = cartProduct?.properties?._uuid;

  const preSelectedOffering = cart?.items?.find(
    item => IsAttachedService(item) && item.properties?._serviceLink === cartProduct?.properties?._serviceLink
  );
  const preSelectedOfferingServiceData = JSON.parse(
    (preSelectedOffering?.properties?._serviceData as string) ?? 'null'
  );
  const preSelectedOfferingId = preSelectedOfferingServiceData?.id?.split(/(mobile.+|extended.+|apple.+)/g)?.[1];

  const hasAttachedService = Boolean(preSelectedOfferingId);
  if (
    isServicesLoading ||
    isPurchasedProductLoading ||
    isSuggestedProductsLoading ||
    (!suggestions?.length && (!serviceSuggestions?.length || hasAttachedService))
  ) {
    return null;
  }
  return (
    <TrackingProvider category="attach" context="cart">
      <div className={styles.container}>
        <CarouselTabs
          borderBottomColor="transparent"
          label="Add: "
          tabsContainerClassName={styles.tabsContainer}
          initialTabIndex={
            attachConfig?.myCartInitialTab === 'none' ? null : attachConfig?.myCartInitialTab === 'products' ? 1 : 0
          }
          canBeUnselected
          tabs={
            [
              ...(!isServicesLoading && !isPurchasedProductLoading && serviceSuggestions?.length && !hasAttachedService
                ? [
                    {
                      renderLabel: (tabProps: TabProps) => (
                        <ChipTab {...getServicesTabProps(serviceSuggestions)} {...tabProps} textAlign="start" />
                      ),
                      contents: (
                        <ProductService
                          key={`${purchasedProductSku}-${preSelectedOfferingId}`}
                          productSku={purchasedProduct?.sku || purchasedProduct?.product?.id || ''}
                          productCartId={productCartId}
                          preSelectedOfferingId={preSelectedOfferingId}
                          gtmData={purchasedProduct?.google_tag_manager ?? {}}
                          suggestions={serviceSuggestions}
                          variant="cart"
                        />
                      ),
                      labelTestId: 'my-cart_attach-tab-label_services',
                      contentsTestId: 'my-cart_attach-tab-contents_services',
                    },
                  ]
                : []),
              ...(isProductsModuleDisabled
                ? []
                : suggestions?.length
                ? [
                    {
                      renderLabel: (tabProps: TabProps) => <ChipTab label="Products" {...tabProps} />,
                      contents: (
                        <EnhancedProductCarousel
                          numProducts={attachConfig?.numProductsPerNonBundleCategory}
                          products={suggestions}
                          slidesOffsetBefore={{ xs: 8, md: 50 }}
                        />
                      ),
                      labelTestId: 'my-cart_attach-tab-label_products',
                      contentsTestId: 'my-cart_attach-tab-contents_products',
                    },
                  ]
                : []),
            ].filter(Boolean) as CarouselTabsProps['tabs']
          }
        />
      </div>
    </TrackingProvider>
  );
};

const getServicesTabProps = (serviceSuggestions: Array<LineItemProps>): Pick<ChipTabProps, 'label' | 'subText'> => {
  const services = serviceSuggestions?.[0]?.services?.filter(s => s.shouldRender);
  const service = services?.find(service => service?.id === 'mobile-extras') ?? services?.[0];
  const serviceOffering = service?.offerings?.[0];

  const isExtraCare = [ServiceIds.ExtraCare, ServiceIds.ExtraCareNz].includes((service?.id ?? '') as ServiceIds);
  const isAppleCareNz = [ServiceIds.AppleCareNz].includes((service?.id ?? '') as ServiceIds);

  let offeringLength =
    serviceOffering?.suggestedWarrantyPeriods
      ?.find(swp => swp?.name?.includes('JB Hi-Fi Extra Care:'))
      ?.period?.replace('(s)', 's') ?? '';

  if (isAppleCareNz) {
    const offeringLengthNumber = parseInt(serviceOffering?.additionalCoveragePeriod?.match(/\d+/)?.[0] ?? '0');
    offeringLength = offeringLengthNumber ? `${offeringLengthNumber} years` : '';
  }

  return {
    label: service?.name?.replace('JB Hi-Fi', '') ?? 'Services',
    subText: serviceOffering?.priceInc
      ? (isAppleCareNz || isExtraCare) && offeringLength
        ? `${offeringLength} for ${formatMoney(serviceOffering.priceInc)}`
        : formatMoney(serviceOffering.priceInc)
      : undefined,
  };
};
